// Project Library — the "back shelf" browsing surface for projects
// (retry chains, as one unit) that an operator moved to Overflow. Deliberately
// simple: no search, no filters, no sorting, no pagination, no tabs.
// If the collection ever becomes unwieldy we can solve that later.
//
// Overflow is a discovery-curation flag. It does NOT change project
// state, participation, provenance, or permissions. This page
// intentionally renders projects whose `libraryPlacement === "overflow"`
// independently of isLibraryVisible() — see Commons Admin v1 amendment
// (Feb 2026).

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useCommons } from "../lib/store";
import ProjectCard from "../components/ProjectCard";
import ProjectCardCompact from "../components/ProjectCardCompact";
import { PastAttemptStack } from "../components/PastAttemptStack";
import { isOverflow } from "../lib/domain";

export default function ProjectLibrary() {
  const nav = useNavigate();
  const { data } = useCommons();

  const overflowProjects = useMemo(() => {
    if (!data) return [];
    // Only projects the admin has moved to Overflow. We do NOT run
    // isLibraryVisible() here on purpose: Project Library exists to
    // surface exactly the projects that isLibraryVisible() hides.
    // Drafts still shouldn't appear (they belong to their author's
    // private working state), so filter those out.
    return data.projects
      // One entry per chain (current attempt); past attempts render beneath it.
      .filter((p) => isOverflow(p, data.projects) && p.status !== "draft" && !p.nextAttemptId)
      .slice()
      .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  }, [data]);

  const openProject = (id) => nav(`/project/${id}`);

  const renderCard = (p) => {
    const status = p.status;
    if (status === "completed" || status === "closed-not-met" || status === "cancelled") {
      return (
        <ProjectCardCompact
          key={p.id}
          project={p}
          pledges={data.pledges}
          people={data.people}
          onClick={() => openProject(p.id)}
        />
      );
    }
    return (
      <ProjectCard
        key={p.id}
        project={p}
        pledges={data.pledges}
        people={data.people}
        onClick={() => openProject(p.id)}
      />
    );
  };

  if (!data) return <div className="c-load">Loading…</div>;

  return (
    <div className="c-page" data-testid="library-page">
      <div className="c-page-topbar">
        <button
          className="c-back-row"
          onClick={() => nav("/profile")}
          data-testid="library-back-btn"
          aria-label="Back to You"
        >
          <ChevronLeft size={18} /> You
        </button>
      </div>

      <div className="c-library-hero">
        <div className="c-library-title" data-testid="library-title">Project Library</div>
        <div className="c-library-sub" data-testid="library-sub">
          More projects in Commons.
        </div>
      </div>

      {overflowProjects.length === 0 ? (
        <div className="c-library-empty" data-testid="library-empty">
          The library is quiet at the moment. Check back soon.
        </div>
      ) : (
        <div className="c-library-list" data-testid="library-list">
          {overflowProjects.map((p) => (
            <div key={p.id} className="c-project-with-history">
              {renderCard(p)}
              <PastAttemptStack project={p} allProjects={data.projects} pledges={data.pledges} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
